import { JWT_KEY } from "@/constants/api";
import { AxiosError } from "axios";

// Define error response structure
export interface ApiErrorResponse {
  error?: string;
  message?: string;
  statusCode?: number;
}

// Define custom error types
export type ApiError = AxiosError<ApiErrorResponse>;

// Enhanced error with additional context
export interface EnhancedError extends Error {
  context: string;
  originalError: unknown;
  statusCode?: number;
  userMessage?: string;
}

/**
 * Checks if an error is an API error (Axios error)
 * @param error - The error to check
 * @returns True if it's an API error, false otherwise
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    error !== null &&
    typeof error === "object" &&
    "isAxiosError" in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Checks if an error is an enhanced error from withErrorHandling
 * @param error - The error to check
 * @returns True if it's an enhanced error, false otherwise
 */
export function isEnhancedError(error: unknown): error is EnhancedError {
  return (
    error instanceof Error && "context" in error && "originalError" in error
  );
}

/**
 * Get an error message based on HTTP status code and context
 * @param statusCode - The HTTP status code
 * @param context - The context of the operation
 * @returns A user-friendly error message
 */
export function getMessageByStatusCode(
  statusCode: number,
  context: string
): string {
  switch (statusCode) {
    case 400:
      return "Missing required fields. Please provide all required information.";
    case 401:
      return "Authentication required. Please log in again.";
    case 403:
      return "You do not have permission to perform this action.";
    case 404:
      return `The item you're trying to access doesn't exist.`;
    case 409:
      if (context.includes("delete")) {
        return "This item cannot be deleted because it has associated items.";
      }
      return "An item with this name already exists.";
    case 500:
      return "Server error. Please try again later.";
    default:
      return `Failed to ${context}.`;
  }
}

/**
 * Get an appropriate error message based on the error
 * @param error - The error from the API or service layer
 * @param context - The context of the operation (e.g., "updating model")
 * @returns A user-friendly error message
 */
export function getErrorMessage(error: unknown, context: string): string {
  // If it's an enhanced error from withErrorHandling
  if (isEnhancedError(error)) {
    // Use the pre-generated user message if available
    if (error.userMessage) {
      return error.userMessage;
    }
  }

  // If it's an Axios error with a response
  if (isApiError(error)) {
    // If the API returned a specific error message, use it
    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    // Otherwise, use status code to determine message
    if (error.response?.status) {
      return getMessageByStatusCode(error.response.status, context);
    }
  }

  // If it's a standard Error object
  if (error instanceof Error) {
    return error.message || `An error occurred while ${context}.`;
  }

  // Generic error message for unknown errors
  return `An unexpected error occurred while ${context}.`;
}

/**
 * Higher-order function to handle errors in service calls
 * @param callback - Async function to execute
 * @param context - Optional context for error tracking
 * @returns Result of the callback function
 * @throws EnhancedError with additional context and user-friendly message
 */
export const withErrorHandling = async <T>(
  callback: () => Promise<T>,
  context?: string
): Promise<T> => {
  try {
    return await callback();
  } catch (error: unknown) {
    console.error(
      `Error in service call${context ? ` (${context})` : ""}:`,
      error instanceof Error ? error.message : error
    );

    // Create enhanced error with additional context
    const enhancedError = new Error(
      error instanceof Error ? error.message : "Unknown error"
    ) as EnhancedError;

    enhancedError.context = context || "Unknown context";
    enhancedError.originalError = error;

    // Add user-friendly message based on error type
    if (isApiError(error)) {
      // Extract status code if available
      const statusCode = error.response?.status;
      enhancedError.statusCode = statusCode;

      // Handle specific error responses
      if (error.response?.data?.error) {
        enhancedError.userMessage = error.response.data.error;
      } else if (statusCode) {
        enhancedError.userMessage = getMessageByStatusCode(
          statusCode,
          context || ""
        );

        // Handle 401 Unauthorized errors by redirecting to login page
        if (statusCode === 401) {
          console.log(
            "Unauthorized access detected. Redirecting to login page..."
          );
          localStorage.removeItem(JWT_KEY);
          window.location.href = `/surfr-ui/login`;
        }
      }
    }

    // Set default user message if none was determined
    if (!enhancedError.userMessage) {
      enhancedError.userMessage = `An unexpected error occurred while ${
        context || "performing this operation"
      }.`;
    }

    throw enhancedError;
  }
};
